import type { BriefTaskAccent } from './briefTasks';

/**
 * Composant ligne BriefTask (MVP2.5 T6).
 *
 * Helper `buildTasksOfTheDay` et types associés dans `./briefTasks.ts`
 * (séparés pour respecter `react-refresh/only-export-components`).
 */

export function BriefTask({
  done,
  active = false,
  label,
  counter,
  accent,
  xp,
  onClick,
}: {
  done: boolean;
  /** Step en cours (première non-done) — applique le scan-line acid. */
  active?: boolean;
  label: string;
  /** "done_today / target_today" — texte rendu à côté du label. */
  counter: string;
  accent: BriefTaskAccent;
  xp: number;
  onClick: () => void;
}): JSX.Element {
  const accentColor = {
    violet: 'var(--violet-500)',
    cyan: 'var(--cyan-500)',
    amber: 'var(--amber-500)',
    coral: 'var(--coral-500)',
    orange: 'var(--orange-500)',
    // 'paper' = accent neutre (cross-module, ex: Revision Cards 0.8.0).
    paper: 'var(--paper-50)',
  }[accent];
  // step-validate joue 700ms quand done = true (one-shot). active applique le
  // pseudo-element scan-line acid 4s infinite (cf. globals.css).
  const stateClass = done
    ? 'border-volt-500/40 bg-volt-500/[0.04] animate-step-validate'
    : active
      ? 'mission-step-active border-acid-500 bg-acid-500/[0.05] shadow-[0_0_0_1px_rgba(198,255,58,0.15),0_0_24px_-6px_rgba(198,255,58,0.4)]'
      : 'border-ink-700 hover:border-ink-600 bg-ink-900/60 hover:bg-ink-900';
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`relative overflow-visible w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border transition-all duration-200 ${stateClass}`}
      >
        <span
          className="size-5 rounded-md border flex items-center justify-center font-mono text-xs"
          style={{ borderColor: accentColor, color: done ? accentColor : 'transparent' }}
        >
          {done ? '✓' : ''}
        </span>
        <span className={`flex-1 text-sm ${done ? 'text-ink-300 line-through' : 'text-paper-50'}`}>{label}</span>
        <span
          className="font-mono text-xs tabular-nums text-ink-300"
          aria-label={`Avancement ${counter}`}
        >
          {counter}
        </span>
        <span
          className={`font-mono text-xs tabular-nums px-1.5 py-0.5 rounded border ${
            done ? 'border-volt-500/40 text-volt-500 bg-volt-500/10' : 'border-ink-700 text-ink-300'
          }`}
        >
          {done ? `+${xp}` : `${xp}`} pts
        </span>
        <span className="text-ink-300 text-xs">→</span>
      </button>
    </li>
  );
}
